import React, { useEffect, useState, useRef } from "react";
import api from "../Service/api";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import "./ExpensesByDate.css";

function ExpensesByDate() {
  const [data, setData] = useState({});
  const [summary, setSummary] = useState({
    totalReceivableAmount: 0,
    totalExpensesAmount: 0,
    currentBalance: 0,
    currency: "PKR"
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [categoriesOptions, setCategoriesOptions] = useState({});
  const [isPrinting, setIsPrinting] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [rawResponse, setRawResponse] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // ========== NEW: Category filter states ==========
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoryInput, setCategoryInput] = useState("");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const categoryRef = useRef(null);

  const printRef = useRef();

  // Parse a date string safely as local date.
// Handles "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss", and Date objects.
const parseLocalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  const str = String(value);

  // If it's a plain date (no time), split and build a local Date
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d); // local midnight
  }

  // If it has a time component, strip the "Z" so it's parsed as local,
  // OR use the date part only — depending on what you want.
  // Here we still parse just the date part to avoid TZ shifts:
  if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
    const [y, m, d] = str.substring(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  const fallback = new Date(str);
  return isNaN(fallback.getTime()) ? null : fallback;
};

  // ========== Fetch all distinct categories for dropdown ==========
  const fetchAllCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await api.get("/Expenses/categories");
      if (res.data && res.data.code === 1) {
        setAllCategories(res.data.additionalDetail || []);
      }
    } catch (err) {
      console.error("Failed to fetch categories", err);
    } finally {
      setLoadingCategories(false);
    }
  };

  // ========== Main fetch — supports category filter ==========
  const fetchExpenses = async () => {
    setLoading(true);
    try {
      // If a category is selected, use the dedicated category endpoint
      if (selectedCategory) {
        const params = { category: selectedCategory };

        // Optional: also support date range with category
        if (startDate && endDate) {
          const res = await api.get("/Expenses/by-category-date", {
            params: { category: selectedCategory, start: startDate, end: endDate }
          });
          processResponse(res.data, true);
          return;
        }

        const res = await api.get("/Expenses/by-category", { params });
        processResponse(res.data, true);
        return;
      }

      // Default: fetch all expenses (existing behavior)
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const res = await api.get("/Expenses/get/expenses", { params });
      processResponse(res.data, false);
    } catch (err) {
      console.error("Failed to fetch expenses", err);
      resetData();
    } finally {
      setLoading(false);
    }
  };

  // ========== Process the API response ==========
  const processResponse = (responseData, isCategoryResponse) => {
    if (!responseData) {
      resetData();
      return;
    }

    setRawResponse(responseData);

    if (isCategoryResponse) {
      // Flat list of expenses — group by date on the client
      const list = Array.isArray(responseData.additionalDetail)
        ? responseData.additionalDetail
        : [];

      const grouped = {};
      list.forEach(e => {
        const dateKey = e.expenseDate || e.date;
        if (!dateKey) return;
        if (!grouped[dateKey]) grouped[dateKey] = [];
        grouped[dateKey].push(e);
      });

      // Sort each group by id
      Object.keys(grouped).forEach(d => {
        grouped[d].sort((a, b) => (a.id || 0) - (b.id || 0));
      });

      setData(grouped);
      setTransactions([]);

      // Compute the sum of ONLY the filtered expenses
      const filteredExpensesTotal = list.reduce(
        (sum, e) => sum + (parseFloat(e.amount) || 0), 0
      );

      // Keep the original Total Receivable and Total Expenses from the summary,
      // but calculate a new Remaining Balance based on the filtered view.
      setSummary(prevSummary => {
        const originalReceivable = prevSummary.totalReceivable || 0;
        const originalTotalExpenses = prevSummary.totalExpenseDeductions || 0;
        
        // If you want the Remaining Balance to reflect the filtered expenses:
        // Remaining = Total Receivable - Filtered Expenses
        const newBalance = originalReceivable - filteredExpensesTotal;

        return {
          ...prevSummary, // Keep currency, original totals, etc.
          currentBalance: newBalance // Update only the balance
        };
      });
      
    } else {
      // Existing full response format
      setSummary({
        totalReceivable: responseData.summary?.totalReceivable || 0,
        totalExpenseDeductions: responseData.summary?.totalExpenseDeductions || 0,
        currentBalance: responseData.summary?.currentBalance || 0,
        currency: responseData.summary?.currency || "PKR",
        latestDate: responseData.summary?.latestDate || null
      });

      const sortedTransactions = (responseData.transactionHistory || [])
        .sort((a, b) =>
          parseLocalDate(a.transactionDate || a.date) - parseLocalDate(b.transactionDate || b.date)
        );
      setTransactions(sortedTransactions);

      const combinedExpenses = {};

      if (responseData.PRODUCTIVE && responseData.PRODUCTIVE.expenses) {
        Object.entries(responseData.PRODUCTIVE.expenses).forEach(([date, expenses]) => {
          if (!combinedExpenses[date]) combinedExpenses[date] = [];
          if (Array.isArray(expenses) && expenses.length > 0) {
            combinedExpenses[date].push(...expenses);
          }
        });
      }

      if (responseData.NON_PRODUCTIVE && responseData.NON_PRODUCTIVE.expenses) {
        Object.entries(responseData.NON_PRODUCTIVE.expenses).forEach(([date, expenses]) => {
          if (!combinedExpenses[date]) combinedExpenses[date] = [];
          if (Array.isArray(expenses) && expenses.length > 0) {
            combinedExpenses[date].push(...expenses);
          }
        });
      }

      Object.keys(combinedExpenses).forEach(date => {
        combinedExpenses[date].sort((a, b) => (a.id || 0) - (b.id || 0));
      });

      setData(combinedExpenses);
    }
  };

  const resetData = () => {
    setData({});
    setRawResponse(null);
    setSummary({
      totalReceivable: 0,
      totalExpenseDeductions: 0,
      currentBalance: 0,
      currency: "PKR"
    });
    setTransactions([]);
  };

  // Initial load
  useEffect(() => {
    fetchAllCategories();
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when category changes
  useEffect(() => {
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryRef.current && !categoryRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCategories = async (type, expenseId) => {
    try {
      const res = await api.get("/Expenses/get/category", { params: { type } });
      setCategoriesOptions((prev) => ({ ...prev, [expenseId]: res.data || [] }));
    } catch (err) {
      console.error("Failed to fetch categories for expense id", expenseId, err);
    }
  };

  const handleEdit = (expense) => {
    setEditingId(expense.id);
    setEditValues({ ...expense });
    fetchCategories(expense.expenseType || "DAILY", expense.id);
  };

  const handleChange = (field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));

    if (field === "expenseType") {
      setEditValues((prev) => ({ ...prev, category: "" }));
      fetchCategories(value, editingId);
    }
  };

  const handleSave = async () => {
    try {
      await api.put(`/Expenses/updateExpenses/${editingId}`, editValues);
      setEditingId(null);
      setEditValues({});
      fetchExpenses();
    } catch (err) {
      console.error("Failed to update expense", err);
      alert("Failed to update expense");
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this expense?")) return;

    try {
      await api.delete(`/Expenses/delete/${id}`);
      fetchExpenses();
    } catch (err) {
      console.error("Failed to delete expense", err);
      alert("Failed to delete expense");
    }
  };

  // ========== Category dropdown handlers ==========
  const handleCategorySelect = (catName) => {
    setSelectedCategory(catName);
    setCategoryInput(catName);
    setShowCategoryDropdown(false);
  };

  const clearCategoryFilter = () => {
    setSelectedCategory("");
    setCategoryInput("");
    setShowCategoryDropdown(false);
    // useEffect on selectedCategory will re-fetch all
  };

  const filteredCategories = allCategories.filter(cat =>
    cat.toLowerCase().includes(categoryInput.toLowerCase())
  );

  const handlePrint = () => {
    if (isPrinting) return;
    setIsPrinting(true);

    const printWindow = window.open('', '_blank');
    const printContent = printRef.current;
    const printHtml = printContent.outerHTML;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Expenses Report</title>
          <style>
            @media print {
              @page { size: A4; margin: 15mm; }
              body { margin: 0; padding: 20px; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; }
              .print-header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
              .print-header h1 { margin: 0; color: #333; }
              .print-header p { margin: 5px 0; color: #666; }
              .print-date-range { margin-bottom: 20px; padding: 10px; background-color: #f5f5f5; border-radius: 4px; }
              .summary-cards { display: flex; justify-content: space-between; margin-bottom: 20px; gap: 15px; }
              .summary-card { flex: 1; padding: 15px; border: 1px solid #ddd; border-radius: 8px; text-align: center; }
              .summary-card h4 { margin: 0 0 10px 0; color: #555; }
              .summary-card .amount { font-size: 24px; font-weight: bold; margin: 0; }
              .positive { color: #28a745; }
              .negative { color: #dc3545; }
              table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; font-weight: bold; }
              .amount, .balance { text-align: right; }
              .date-card { margin-bottom: 30px; page-break-inside: avoid; }
              .date-header { background-color: #e8f5e9; padding: 10px; font-weight: bold; margin-bottom: 10px; border-radius: 4px; }
              .day-total { text-align: right; margin-top: 10px; padding-top: 10px; border-top: 2px solid #ddd; font-weight: bold; }
              .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
              .badge { display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 12px; }
              .badge.daily { background-color: #2196f3; color: white; }
              .badge.monthly { background-color: #ff9800; color: white; }
              .badge.productive { background-color: #28a745; color: white; }
              .badge.non-productive { background-color: #dc3545; color: white; }
              .badge.receivable { background-color: #28a745; color: white; }
              .badge.expense { background-color: #dc3545; color: white; }
            }
          </style>
        </head>
        <body>${printHtml}</body>
      </html>
    `);

    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
      setIsPrinting(false);
    };
  };

  const exportToExcel = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Expense Management';
      workbook.created = new Date();

      // ========== SHEET 1: SUMMARY ==========
      const summarySheet = workbook.addWorksheet('Summary', { properties: { tabColor: { argb: 'FF2E7D32' } } });
      summarySheet.columns = [
        { header: 'Metric', key: 'metric', width: 28 },
        { header: 'Value', key: 'value', width: 25 },
      ];

      const summaryRows = [
        { metric: '💰 Total Receivable', value: `${summary.currency} ${formatAmount(summary.totalReceivable)}` },
        { metric: '📉 Total Expenses', value: `${summary.currency} ${formatAmount(summary.totalExpenseDeductions)}` },
        { metric: '💵 Remaining Balance', value: `${summary.currency} ${formatAmount(summary.currentBalance)}` },
        { metric: '✅ Productive Expenses', value: `${summary.currency} ${formatAmount(productiveTotal)}` },
        { metric: '❌ Non-Productive Expenses', value: `${summary.currency} ${formatAmount(nonProductiveTotal)}` },
        { metric: '📊 Productivity Ratio', value: `${productiveTotal + nonProductiveTotal > 0 ? ((productiveTotal / (productiveTotal + nonProductiveTotal)) * 100).toFixed(1) : 0}%` },
        { metric: '📂 Category Filter', value: selectedCategory || 'All Categories' },
        { metric: '📅 Date Range', value: getDateRangeText() },
        { metric: '🕒 Report Generated', value: new Date().toLocaleString() },
      ];
      summarySheet.addRows(summaryRows);

      const headerRow = summarySheet.getRow(1);
      headerRow.eachCell(cell => {
        cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E7E34' } };
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
      summarySheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell(cell => {
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            cell.alignment = { vertical: 'middle' };
            if (cell.col === 2) cell.alignment = { horizontal: 'right', vertical: 'middle' };
          });
        }
      });

      // ========== SHEET 2: TRANSACTIONS ==========
      if (transactions && transactions.length > 0) {
        const transSheet = workbook.addWorksheet('Transactions', { properties: { tabColor: { argb: 'FF1976D2' } } });
        transSheet.columns = [
          { header: 'Date', key: 'date', width: 15 },
          { header: 'Type', key: 'type', width: 18 },
          { header: 'Description', key: 'description', width: 45 },
          { header: 'Amount', key: 'amount', width: 20 },
          { header: 'Running Balance', key: 'balance', width: 22 },
        ];

        transactions.forEach(t => {
          transSheet.addRow({
date: t.transactionDate ? parseLocalDate(t.transactionDate)?.toLocaleDateString() : '-',
            type: t.type || '-',
            description: t.description || '-',
            amount: t.amount || 0,
            balance: `${summary.currency} ${formatAmount(t.runningBalance || 0)}`,
          });
        });

        const transHeader = transSheet.getRow(1);
        transHeader.eachCell(cell => {
          cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        transSheet.eachRow((row, rowNumber) => {
          if (rowNumber > 1) {
            row.eachCell(cell => {
              cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
              cell.alignment = { vertical: 'middle' };
              if (cell.col === 4 || cell.col === 5) cell.alignment = { horizontal: 'right', vertical: 'middle' };
            });
          }
        });
        transSheet.getColumn(4).numFmt = '#,##0.00';
        transSheet.getColumn(5).numFmt = '#,##0.00';
      }

      // ========== SHEET 3: EXPENSES BY DATE ==========
      const expenseSheet = workbook.addWorksheet('Expenses by Date', { properties: { tabColor: { argb: 'FFC2185B' } } });
      expenseSheet.columns = [
        { header: 'Date', key: 'date', width: 18 },
        { header: '#', key: 'id', width: 8 },
        { header: 'Category', key: 'category', width: 22 },
        { header: 'Type', key: 'type', width: 14 },
        { header: 'Productivity', key: 'productivity', width: 20 },
        { header: 'Description', key: 'description', width: 40 },
        { header: 'Amount', key: 'amount', width: 18 },
      ];

      const sortedDates = Object.keys(data)
  .filter(d => Array.isArray(data[d]) && data[d].length > 0)
  .sort((a, b) => parseLocalDate(a) - parseLocalDate(b));

      let grandTotal = 0;
      let grandProductive = 0;
      let grandNonProductive = 0;

      sortedDates.forEach(date => {
        const expenses = data[date];
        let dailyProductive = 0;
        let dailyNonProductive = 0;
        let dailyTotal = 0;

        expenses.forEach(e => {
          const amount = e.amount || 0;
          dailyTotal += amount;
          if (e.productivityType === 'PRODUCTIVE') dailyProductive += amount;
          else if (e.productivityType === 'NON_PRODUCTIVE') dailyNonProductive += amount;

          expenseSheet.addRow({
            date: parseLocalDate(date)?.toLocaleDateString(),
            id: e.id || '',
            category: e.category || '-',
            type: e.expenseType || 'DAILY',
            productivity: e.productivityType || 'PRODUCTIVE',
            description: e.description || '-',
            amount: amount,
          });
        });

        const subRow = expenseSheet.addRow({
  date: `Subtotal for ${parseLocalDate(date)?.toLocaleDateString()}`,
          id: '',
          category: '',
          type: '',
          productivity: `✅ ${dailyProductive} | ❌ ${dailyNonProductive}`,
          description: '',
          amount: dailyTotal,
        });
        subRow.eachCell(cell => {
          cell.font = { bold: true };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } };
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          if (cell.col === 7) cell.alignment = { horizontal: 'right', vertical: 'middle' };
        });
        expenseSheet.addRow({});

        grandTotal += dailyTotal;
        grandProductive += dailyProductive;
        grandNonProductive += dailyNonProductive;
      });

      const grandRow = expenseSheet.addRow({
        date: 'GRAND TOTAL',
        id: '',
        category: selectedCategory || '',
        type: '',
        productivity: `✅ ${grandProductive} | ❌ ${grandNonProductive}`,
        description: '',
        amount: grandTotal,
      });
      grandRow.eachCell(cell => {
        cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E7E34' } };
        cell.border = { top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: { style: 'thin' } };
        if (cell.col === 7) cell.alignment = { horizontal: 'right', vertical: 'middle' };
      });

      const expHeader = expenseSheet.getRow(1);
      expHeader.eachCell(cell => {
        cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC2185B' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });

      expenseSheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1 && !row.getCell(1).value?.toString().startsWith('Subtotal') && rowNumber !== expenseSheet.rowCount) {
          row.eachCell(cell => {
            if (!cell.fill) {
              cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            }
            cell.alignment = { vertical: 'middle' };
            if (cell.col === 7) cell.alignment = { horizontal: 'right', vertical: 'middle' };
          });
        }
      });

      expenseSheet.getColumn(7).numFmt = '#,##0.00';

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const catSuffix = selectedCategory ? `_${selectedCategory.replace(/\s+/g, '_')}` : '';
      const fileName = `expenses_report${catSuffix}_${new Date().toISOString().slice(0,10)}.xlsx`;
      saveAs(blob, fileName);

    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to export Excel. Check console.");
    } finally {
      setIsExporting(false);
    }
  };

  const calculateGrandTotal = () => {
    let grandTotal = 0;
    if (data && typeof data === 'object') {
      Object.values(data).forEach(expenses => {
        if (Array.isArray(expenses)) {
          grandTotal += expenses.reduce((sum, e) => sum + (e?.amount || 0), 0);
        }
      });
    }
    return grandTotal;
  };

  const getDateRangeText = () => {
  if (startDate && endDate) {
    return `${parseLocalDate(startDate)?.toDateString()} - ${parseLocalDate(endDate)?.toDateString()}`;
  } else if (startDate) {
    return `From ${parseLocalDate(startDate)?.toDateString()}`;
  } else if (endDate) {
    return `Until ${parseLocalDate(endDate)?.toDateString()}`;
  }
  return "All Time";
};

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return "0";
    return Number(amount).toLocaleString();
  };

  const calculateProductivityTotals = () => {
    // When category filter is active, compute from flat grouped data
    if (selectedCategory) {
      let productive = 0;
      let nonProductive = 0;
      Object.values(data).forEach(list => {
        if (Array.isArray(list)) {
          list.forEach(e => {
            if (e.productivityType === 'PRODUCTIVE') productive += e.amount || 0;
            else if (e.productivityType === 'NON_PRODUCTIVE') nonProductive += e.amount || 0;
          });
        }
      });
      return { productiveTotal: productive, nonProductiveTotal: nonProductive };
    }

    if (rawResponse) {
      return {
        productiveTotal: rawResponse.PRODUCTIVE?.total || 0,
        nonProductiveTotal: rawResponse.NON_PRODUCTIVE?.total || 0
      };
    }
    return { productiveTotal: 0, nonProductiveTotal: 0 };
  };

  const { productiveTotal, nonProductiveTotal } = calculateProductivityTotals();

  return (
    <div className="expenses-wrapper">
      <h2 className="page-title">📊 Expenses & Receivable Summary</h2>

      {/* SUMMARY CARDS */}
      <div className="summary-cards">
        <div className="summary-card receivable">
          <h4>💰 Total Receivable</h4>
          <div className="amount positive">
            {summary.currency} {formatAmount(summary.totalReceivable)}
          </div>
        </div>

        <div className="summary-card expense">
          <h4>📉 Total Expenses</h4>
          <div className="amount negative">
            {summary.currency} {formatAmount(summary.totalExpenseDeductions)}
          </div>
        </div>

        <div className="summary-card balance">
          <h4>💵 Remaining Balance</h4>
          <div className={`amount ${summary.currentBalance >= 0 ? 'positive' : 'negative'}`}>
            {summary.currency} {formatAmount(summary.currentBalance)}
          </div>
          <small>Available for expenses</small>
        </div>
      </div>

      {/* Productivity Summary Cards */}
      <div className="productivity-summary-cards">
        <div className="summary-card productive">
          <h4>✅ PRODUCTIVE Expenses</h4>
          <div className="amount positive">
            {summary.currency} {formatAmount(productiveTotal)}
          </div>
          <small>Value-adding expenses</small>
        </div>

        <div className="summary-card non-productive">
          <h4>❌ NON-PRODUCTIVE Expenses</h4>
          <div className="amount negative">
            {summary.currency} {formatAmount(nonProductiveTotal)}
          </div>
          <small>Non value-adding expenses</small>
        </div>

        <div className="summary-card ratio">
          <h4>📊 Productivity Ratio</h4>
          <div className="amount">
            {productiveTotal + nonProductiveTotal > 0
              ? `${((productiveTotal / (productiveTotal + nonProductiveTotal)) * 100).toFixed(1)}%`
              : '0%'}
          </div>
          <small>Productive vs Total</small>
        </div>
      </div>

      {/* ========== FILTER BAR (with new Category search) ========== */}
      <div className="filter-bar">
        {/* CATEGORY SEARCH DROPDOWN */}
        <div className="filter-field" style={{ position: 'relative', minWidth: '220px' }} ref={categoryRef}>
          <label>Category</label>
          <input
            type="text"
            value={categoryInput}
            placeholder="Type to search category..."
            onChange={(e) => {
              setCategoryInput(e.target.value);
              setShowCategoryDropdown(true);
              if (e.target.value === "") {
                setSelectedCategory("");
              }
            }}
            onFocus={() => setShowCategoryDropdown(true)}
            disabled={loadingCategories}
          />
          {showCategoryDropdown && (
            <div
              style={{
                position: 'absolute', top: '100%', left: 0, right: 0,
                maxHeight: '250px', overflowY: 'auto', backgroundColor: 'white',
                border: '1px solid #ccc', borderRadius: '4px', zIndex: 1000,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)', marginTop: '2px'
              }}
            >
              <div
                style={{
                  padding: '10px', cursor: 'pointer', borderBottom: '1px solid #eee',
                  backgroundColor: !selectedCategory ? '#e8f5e9' : 'white',
                  fontWeight: 'bold'
                }}
                onMouseDown={(e) => { e.preventDefault(); clearCategoryFilter(); }}
              >
                All Categories
              </div>
              {filteredCategories.length === 0 && (
                <div style={{ padding: '10px', color: '#888', fontStyle: 'italic' }}>
                  No categories found
                </div>
              )}
              {filteredCategories.map((cat, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '10px', cursor: 'pointer', borderBottom: '1px solid #eee',
                    backgroundColor: selectedCategory === cat ? '#e8f5e9' : 'white'
                  }}
                  onMouseDown={(e) => { e.preventDefault(); handleCategorySelect(cat); }}
                >
                  {cat}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="filter-field">
          <label>Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="filter-field">
          <label>End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <button className="filter-btn" onClick={fetchExpenses}>Apply Filter</button>
        <button
          className="clear-btn"
          onClick={() => {
            setStartDate("");
            setEndDate("");
            setSelectedCategory("");
            setCategoryInput("");
            // fetch on next tick after state clears
            setTimeout(fetchExpenses, 0);
          }}
        >
          Clear
        </button>
        <button className="print-btn" onClick={handlePrint} disabled={isPrinting}>
          🖨️ {isPrinting ? "Preparing..." : "Print"}
        </button>
        <button className="toggle-transactions-btn" onClick={() => setShowTransactions(!showTransactions)}>
          {showTransactions ? "Hide" : "Show"} Transactions
        </button>
        <button
          className="export-excel-btn"
          onClick={exportToExcel}
          disabled={isExporting || loading}
          style={{
            padding: '8px 16px', backgroundColor: '#217346', color: 'white',
            border: 'none', borderRadius: '4px',
            cursor: (isExporting || loading) ? 'not-allowed' : 'pointer',
            opacity: (isExporting || loading) ? 0.6 : 1
          }}
        >
          📊 {isExporting ? "Exporting..." : loading ? "Loading..." : "Export to Excel"}
        </button>
      </div>

      {/* Active filter badge */}
      {selectedCategory && (
        <div style={{ marginBottom: '12px', fontSize: '13px', color: '#555', fontStyle: 'italic' }}>
          Filtered by Category: <strong>{selectedCategory}</strong>
        </div>
      )}

      {/* TRANSACTIONS SECTION */}
      {showTransactions && transactions && transactions.length > 0 && (
        <div className="transactions-section">
          <h3>📜 Transaction History (with Running Balance)</h3>
          <div className="transactions-table-container">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th className="amount">Amount</th>
                  <th className="balance">Running Balance</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction, index) => (
                  <tr key={transaction.id || index} className="transaction-row">
                    <td>
  {transaction.transactionDate
    ? parseLocalDate(transaction.transactionDate)?.toLocaleDateString()
    : transaction.date
      ? parseLocalDate(transaction.date)?.toLocaleDateString()
      : '-'}
</td>
                    <td>
                      <span className={`badge ${transaction.type === 'RECEIVABLE' ? 'receivable' : 'expense'}`}>
                        {transaction.type === 'RECEIVABLE' ? '➕ Receivable' : '➖ Expense'}
                      </span>
                    </td>
                    <td className="description-cell">
                      {transaction.description && transaction.description.includes('Expense Update [ID:') ? (
                        <>
                          {transaction.description.split(']:')[0] + ']'}
                          <small style={{ color: '#007bff', fontWeight: 'bold' }}>
                            {transaction.description.split(']:')[0].split('[ID:')[1] &&
                              ` (Expense ${transaction.description.split('[ID:')[1].split(']')[0]})`}
                          </small>
                          {transaction.description.split(']:')[1]}
                        </>
                      ) : (
                        transaction.description || '-'
                      )}
                      {transaction.type === 'EXPENSE_DEDUCTION' && transaction.expenseDetails && (
                        <small className="expense-details"> ({transaction.expenseDetails})</small>
                      )}
                    </td>
                    <td className="amount">
                      <span className={transaction.amount > 0 ? 'positive' : 'negative'}>
                        {transaction.type === 'RECEIVABLE'
                          ? `+ ${formatAmount(Math.abs(transaction.amount))}`
                          : `- ${formatAmount(Math.abs(transaction.amount))}`}
                      </span>
                    </td>
                    <td className="balance">
                      <strong>
                        {summary.currency} {formatAmount(transaction.runningBalance || 0)}
                      </strong>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan="3"><strong>Final Balance</strong></td>
                  <td className="amount"></td>
                  <td className="balance">
                    <strong className={summary.currentBalance >= 0 ? 'positive' : 'negative'}>
                      {summary.currency} {formatAmount(summary.currentBalance)}
                    </strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* LOADING */}
      {loading && <p className="empty-text">Loading expenses...</p>}

      {/* NO DATA */}
      {!loading && (!data || Object.keys(data).length === 0) && (
        <p className="empty-text">
          {selectedCategory
            ? `No expenses found for category "${selectedCategory}"`
            : "No expenses found"}
        </p>
      )}

      {/* HIDDEN PRINT SECTION */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          <div className="print-header">
            <h1>Expenses & Receivable Report</h1>
            <p>Generated on: {new Date().toLocaleString()}</p>
            {selectedCategory && <p><strong>Category:</strong> {selectedCategory}</p>}
          </div>

          <div className="print-date-range">
            <strong>Date Range:</strong> {getDateRangeText()}
          </div>

          <div className="summary-cards">
            <div className="summary-card">
              <h4>Total Receivable</h4>
              <div className="amount positive">{summary.currency} {formatAmount(summary.totalReceivable)}</div>
            </div>
            <div className="summary-card">
              <h4>Total Expenses</h4>
              <div className="amount negative">{summary.currency} {formatAmount(summary.totalExpenseDeductions)}</div>
            </div>
            <div className="summary-card">
              <h4>Remaining Balance</h4>
              <div className={`amount ${summary.currentBalance >= 0 ? 'positive' : 'negative'}`}>
                {summary.currency} {formatAmount(summary.currentBalance)}
              </div>
            </div>
          </div>

          <div className="summary-cards">
            <div className="summary-card">
              <h4>Productive Expenses</h4>
              <div className="amount positive">{summary.currency} {formatAmount(productiveTotal)}</div>
            </div>
            <div className="summary-card">
              <h4>Non-Productive Expenses</h4>
              <div className="amount negative">{summary.currency} {formatAmount(nonProductiveTotal)}</div>
            </div>
          </div>

          {transactions && transactions.length > 0 && (
            <>
              <h3>Transaction History</h3>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th className="amount">Amount</th>
                    <th className="balance">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction, index) => (
                    <tr key={transaction.id || index}>
<td>{transaction.transactionDate ? parseLocalDate(transaction.transactionDate)?.toLocaleDateString() : '-'}</td>                      <td>{transaction.type}</td>
                      <td>{transaction.description || '-'}</td>
                      <td className="amount">{formatAmount(Math.abs(transaction.amount))}</td>
                      <td className="balance">{summary.currency} {formatAmount(transaction.runningBalance || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {data && typeof data === 'object' && Object.entries(data)
            .filter(([date, expenses]) => Array.isArray(expenses) && expenses.length > 0)
.sort(([dateA], [dateB]) => parseLocalDate(dateA) - parseLocalDate(dateB))
            .map(([date, expenses]) => {
              if (!Array.isArray(expenses) || expenses.length === 0) return null;
              const total = expenses.reduce((sum, e) => sum + (e?.amount || 0), 0);

              return (
                <div key={date} className="date-card">
<div className="date-header">📅 {parseLocalDate(date)?.toDateString()}</div>       
           <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Category</th>
                        <th>Type</th>
                        <th>Productivity</th>
                        <th>Description</th>
                        <th className="amount">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map((e, idx) => (
                        <tr key={e.id}>
                          <td>{idx + 1}</td>
                          <td>{e.category}</td>
                          <td><span className={`badge ${e.expenseType === "MONTHLY" ? "monthly" : "daily"}`}>{e.expenseType || "DAILY"}</span></td>
                          <td><span className={`badge ${e.productivityType === "PRODUCTIVE" ? "productive" : "non-productive"}`}>{e.productivityType || "PRODUCTIVE"}</span></td>
                          <td>{e.description || "-"}</td>
                          <td className="amount">{formatAmount(e.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="day-total">Total: {formatAmount(total)}</div>
                </div>
              );
            })}

          <div className="footer">
            <p>Grand Total Expenses: {formatAmount(calculateGrandTotal())}</p>
            <p>Productive Total: {formatAmount(productiveTotal)}</p>
            <p>Non-Productive Total: {formatAmount(nonProductiveTotal)}</p>
            <p>Remaining Balance: {summary.currency} {formatAmount(summary.currentBalance)}</p>
          </div>
        </div>
      </div>

      {/* MAIN DISPLAY SECTION */}
      {!loading && data && typeof data === 'object' && Object.keys(data).length > 0 &&
        Object.entries(data)
          .filter(([date, expenses]) => Array.isArray(expenses) && expenses.length > 0)
.sort(([dateA], [dateB]) => parseLocalDate(dateA) - parseLocalDate(dateB))
          .map(([date, expenses]) => {
            if (!Array.isArray(expenses) || expenses.length === 0) return null;

            const total = expenses.reduce((sum, e) => sum + (e?.amount || 0), 0);

            let dailyProductiveTotal = 0;
            let dailyNonProductiveTotal = 0;
            expenses.forEach(e => {
              if (e.productivityType === 'PRODUCTIVE') dailyProductiveTotal += e.amount || 0;
              else if (e.productivityType === 'NON_PRODUCTIVE') dailyNonProductiveTotal += e.amount || 0;
            });

            return (
              <div key={date} className="date-card">
                <div className="date-header">
                  📅 {parseLocalDate(date)?.toDateString()}
                  <div className="date-stats">
                    <span className="productive-stats">✅ Productive: {formatAmount(dailyProductiveTotal)}</span>
                    <span className="non-productive-stats">❌ Non-Productive: {formatAmount(dailyNonProductiveTotal)}</span>
                  </div>
                </div>
<div className="expenses-table-wrapper">
                <table className="expenses-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Category</th>
                      <th>Type</th>
                      <th>Productivity</th>
                      <th>Description</th>
                      <th className="amount">Amount</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((e) => (
                      <tr key={e.id} className={`expense-row ${e.productivityType === 'PRODUCTIVE' ? 'productive-row' : 'non-productive-row'}`}>
                        <td>{e.id}</td>

                        <td>
                          {editingId === e.id ? (
                            <select
                              value={editValues.category || ""}
                              onChange={(ev) => handleChange("category", ev.target.value)}
                            >
                              <option value="">-- Select Category --</option>
                              {(categoriesOptions[e.id] || []).map((cat) => (
                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                              ))}
                            </select>
                          ) : (
                            e.category
                          )}
                        </td>

                        <td>
                          {editingId === e.id ? (
                            <select
                              value={editValues.expenseType || "DAILY"}
                              onChange={(ev) => handleChange("expenseType", ev.target.value)}
                            >
                              <option value="DAILY">Daily</option>
                              <option value="MONTHLY">Monthly</option>
                            </select>
                          ) : (
                            <span className={`badge ${e.expenseType === "MONTHLY" ? "monthly" : "daily"}`}>
                              {e.expenseType || "DAILY"}
                            </span>
                          )}
                        </td>

                        <td>
                          {editingId === e.id ? (
                            <select
                              value={editValues.productivityType || "PRODUCTIVE"}
                              onChange={(ev) => handleChange("productivityType", ev.target.value)}
                              className="productivity-select"
                            >
                              <option value="PRODUCTIVE">✅ PRODUCTIVE</option>
                              <option value="NON_PRODUCTIVE">❌ NON-PRODUCTIVE</option>
                            </select>
                          ) : (
                            <span className={`badge ${e.productivityType === "PRODUCTIVE" ? "productive" : "non-productive"}`}>
                              {e.productivityType === "PRODUCTIVE" ? "✅ PRODUCTIVE" : "❌ NON-PRODUCTIVE"}
                            </span>
                          )}
                        </td>

                        <td>
                          {editingId === e.id ? (
                            <input
                              type="text"
                              value={editValues.description || ""}
                              onChange={(ev) => handleChange("description", ev.target.value)}
                            />
                          ) : (
                            e.description || "-"
                          )}
                        </td>

                        <td className="amount">
                          {editingId === e.id ? (
                            <input
                              type="number"
                              value={editValues.amount || 0}
                              onChange={(ev) => handleChange("amount", ev.target.value)}
                            />
                          ) : (
                            `${formatAmount(e.amount)}`
                          )}
                        </td>

                        <td>
                          {editingId === e.id ? (
                            <>
                              <button className="save-btn" onClick={handleSave}>Save</button>
                              <button className="cancel-btn" onClick={handleCancel}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button className="edit-btn" onClick={() => handleEdit(e)}>Edit</button>
                              <button className="delete-btn" onClick={() => handleDelete(e.id)}>Delete</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>

                <div className="day-total">
                  <div className="total-breakdown">
                    <span>✅ Productive: <strong>{formatAmount(dailyProductiveTotal)}</strong></span>
                    <span>❌ Non-Productive: <strong>{formatAmount(dailyNonProductiveTotal)}</strong></span>
                    <span>📊 Total: <strong>{formatAmount(total)}</strong></span>
                  </div>
                </div>
              </div>
            );
          })}
    </div>
  );
}

export default ExpensesByDate;